import React, { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import {
  BasicContainer,
  BodyText,
  BodyTextLarge,
  BodyTextSmall,
  ButtonProps,
  ClickableIcon,
  FlexCol,
  FlexDynamic,
  FlexRow,
  Loader,
} from '@minecraft.atoms';
import Modal from '@minecraft.modal';
import { useUserContext } from '@blocs.auth';
import { Trans, useTranslation } from '@blocs.i18n';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { AccountBillingInfo } from '@minecraft.graphql-operations';
import { useResumeSubscription } from './useResumeSubscription';

export type ResumeSubscriptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CancelRefundPolicyLink: FC = ({ children }) => (
  <a href="https://corp.castingnetworks.com/terms-of-use" target="_blank" rel="noreferrer">
    {children}
  </a>
);

export const ResumeSubscriptionModal: FC<ResumeSubscriptionModalProps> = React.memo(({ isOpen, onClose }) => {
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const routerHistory = useHistory();

  const { subscription, myAccount } = useUserContext() || {};
  const { firstName, lastName, lastFour, cardExpirationMonth, cardExpirationYear } = useMemo(() => {
    return myAccount?.billingInfo ?? ({} as AccountBillingInfo);
  }, [myAccount?.billingInfo]);
  const { resumeSubscription, resumeSubscriptionMutationLoading, isLoading, isPausedSubscription } =
    useResumeSubscription();

  const onResumeSubscription = useCallback(async () => {
    try {
      const isResumed = await resumeSubscription();
      if (isResumed) {
        onClose();
        showMessage({
          color: 'success',
          message: t('common:resumeSubscription.modal.unpauseSuccess'),
        });
      } else {
        showMessage({
          color: 'danger',
          message: t('common:resumeSubscription.modal.unpauseError'),
        });
      }
    } catch (e) {
      showMessage({
        color: 'danger',
        message: t('common:resumeSubscription.modal.unpauseError'),
      });
    }
  }, [resumeSubscription, onClose, showMessage, t]);

  const isDisabled = resumeSubscriptionMutationLoading || isLoading;

  const handleEditBillingInfo = useCallback(() => {
    onClose();
    routerHistory.push('/membership-billing');
  }, [onClose, routerHistory]);

  const buttons = useMemo((): ButtonProps[] => {
    return [
      {
        label: t('common:button.cancel'),
        variant: 'secondary',
        onClick: onClose,
        disabled: isDisabled,
      },
      {
        label: t('common:button.confirm'),
        onClick: onResumeSubscription,
        disabled: isDisabled,
      },
    ];
  }, [t, onClose, isDisabled, onResumeSubscription]);

  if (!isPausedSubscription) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('common:resumeSubscription.modal.title')}
      modalSize="medium"
      customHeaderPadding={{ left: 2, bottom: 1, right: 15, top: 2, mdProps: { left: 0, right: 0 } }}
      handleToggleClick={onClose}
      buttons={buttons}
      data-testid="unpause-subscription-modal"
    >
      <Loader
        loading={isDisabled}
        LoaderComponent={
          <FlexCol alignItems="stretch" className="cn_w-full" data-testid="unpause-subscription-loader" gap="2">
            <Skeleton width="100%" height="42px" />
            <Skeleton width="100%" height="120px" />
            <Skeleton width="100%" height="80px" />
          </FlexCol>
        }
      >
        <FlexDynamic mx="0" gap="2" mdProps={{ mx: '4' }} direction="column">
          <FlexCol
            gap="1"
            w="full"
            alignItems="start"
            justifyContent="start"
            borderPlacement="bottom"
            className="cn_atom_pt-0 cn_atom_pb-3"
          >
            <BodyText fontWeight="medium">{t('common:resumeSubscription.modal.continueSubscription')}</BodyText>
            <BodyText>{t(`common:planTitle.withKeyword.${subscription?.plan?.billingCode}`)}</BodyText>
          </FlexCol>

          <FlexCol
            gap="3"
            w="full"
            alignItems="start"
            justifyContent="start"
            borderPlacement="bottom"
            className="cn_atom_py-3"
          >
            <FlexRow gap="2" justifyContent="space-between" w="full">
              <BodyTextLarge fontWeight="medium">
                {t('common:resumeSubscription.modal.billingInfoHeader')}
              </BodyTextLarge>
              <ClickableIcon name="edit" color="link" onClick={handleEditBillingInfo} />
            </FlexRow>
            <BasicContainer>
              <BodyText fontWeight="medium">{t('common:resumeSubscription.modal.nameOnCard')}</BodyText>
              <BodyText>{`${firstName} ${lastName}`}</BodyText>
            </BasicContainer>
            <BasicContainer>
              <BodyText fontWeight="medium">{t('common:billing.card.card')}</BodyText>
              <BodyText>{t(`common:paywall.success.cardEnding`, { lastFour })}</BodyText>
            </BasicContainer>
            <BasicContainer>
              <BodyText fontWeight="medium">{t('common:resumeSubscription.modal.expiration')}</BodyText>
              <BodyText>{`${cardExpirationMonth}/${cardExpirationYear}`}</BodyText>
            </BasicContainer>
          </FlexCol>
          <FlexCol gap="3" w="full" alignItems="start" justifyContent="start" className="cn_atom_py-3">
            <BodyTextSmall>
              <Trans i18nKey="common:resumeSubscription.modal.terms" components={{ a: <CancelRefundPolicyLink /> }} />
            </BodyTextSmall>
          </FlexCol>
        </FlexDynamic>
      </Loader>
    </Modal>
  );
});
